import React, { memo, useMemo } from "react";
import type { ChatTab, SearchMatch } from "../shared/mcp-chat-types";
import { getMatchCountByTab } from "./mcp-chat-search";

interface McpChatSearchResultsProps {
  matches: SearchMatch[];
  tabs: ChatTab[];
  currentMatchIndex: number;
  onSelectMatch: (matchIndex: number) => void;
}

const McpChatSearchResults: React.FC<McpChatSearchResultsProps> = ({
  matches,
  tabs,
  currentMatchIndex,
  onSelectMatch
}) => {
  const tabMap = useMemo(() => new Map(tabs.map((t) => [t.id, t.name])), [tabs]);
  const countByTab = useMemo(() => getMatchCountByTab(matches), [matches]);

  // Group matches by tabId, preserving order
  const groups = useMemo(() => {
    const result: Array<{ tabId: string; tabName: string; count: number; matchIndices: number[] }> = [];
    const seen = new Map<string, number>();

    for (let i = 0; i < matches.length; i++) {
      const m = matches[i];
      const groupIdx = seen.get(m.tabId);
      if (groupIdx !== undefined) {
        result[groupIdx].matchIndices.push(i);
      } else {
        seen.set(m.tabId, result.length);
        result.push({
          tabId: m.tabId,
          tabName: tabMap.get(m.tabId) || m.tabName,
          count: countByTab.get(m.tabId) || 0,
          matchIndices: [i]
        });
      }
    }
    return result;
  }, [matches, tabMap, countByTab]);

  return (
    <div className="mcp-chat__search-results">
      {groups.map((group) => (
        <div key={group.tabId} className="mcp-chat__search-results-group">
          <div className="mcp-chat__search-results-group-header">
            <span>{group.tabName}</span>
            <span className="mcp-chat__search-results-group-count">{group.count}</span>
          </div>
          {group.matchIndices.map((matchIdx) => {
            const match = matches[matchIdx];
            return (
              <button
                key={matchIdx}
                className={`mcp-chat__search-result-item${matchIdx === currentMatchIndex ? " mcp-chat__search-result-item--active" : ""}`}
                onClick={() => onSelectMatch(matchIdx)}
              >
                {match.snippet}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default memo(McpChatSearchResults);
